// 🧭 10-02 (Nina: "Loading tips should match the type of space being created"):
// the loading screen's tips for a room made with a purpose (build form v2). A
// purpose with its own pool shows ONLY that pool, so a study room never teaches
// /ship. Storytelling and social rooms, and rooms with no purpose (the classic
// form, older rooms, the House), keep the page's general pools (GameStepPage
// LOADING_HINTS + NONCOMP_HINTS).
// Every line must be TRUE in that room (the 09-24 tip audit): each /verb in a
// pool is a real command that commandFitsPurpose keeps there, pinned by
// purposeTips.test.js. The frame (learn + research) has Files, Notes, People,
// Export and the Share button; AIs read the start of a shared doc (about the
// first 1,500 characters, chatGameApi extractDocumentText → the prompt excerpt).
// Player copy: short, punchy, no em dashes.
import { PURPOSE_KEYS } from './spacePurpose';

// True in every purpose room.
const NEUTRAL = [
  'Scroll up to see what happened before you got here. Every space has history.',
  'Spaces remember. Come back later and the whole conversation is still here.',
  '@mention an AI to pull it into the conversation. It answers you directly.',
  'Tap the 😊 under any message to react.',
];

// Productivity & learning (study group · coworking · tutor) and research rooms.
const FOCUS = [
  'Ask the AIs to quiz you. One question at a time works best.',
  'Share a doc from the Share button. The AIs read the start of it.',
  'Every doc, image and drawing shared here lands in Files.',
  'Notes fill in as you go: decisions, to-dos and open questions.',
  'Export this chat any time. It saves as a file.',
  '/note drops a note card the whole room sees.',
  '/translate spanish turns the room bilingual. Built for practice.',
  '/code opens a live coding sandbox right in the chat.',
  '/listen rainy jazz for some focus music.',
  'Stuck on a bug? Narrate it to the AIs. Their questions help.',
  'Tell the room your goal for today. It helps the AIs help you.',
];

// Research & academia (lab · journal club · deep dive), on top of FOCUS.
const RESEARCH = [
  '/talk two AIs about a paper and let them argue it out.',
  'Ask for the strongest objection to your idea. Then ask again.',
  'Journal club? Paste the abstract first so everyone starts in the same place.',
];

// Personal & life (advice · motivation · AITA · relationships).
const CALM = [
  'Say it the way you would to a friend. The AIs answer the same way.',
  'Want it straight? Say "be honest, not nice."',
  'Rehearse a hard conversation here before the real one.',
  'AITA? Give both sides. The jury is fairer for it.',
  'The crowd chat sits beside you. Read it or ignore it.',
  '@mention one AI to hear just their take.',
];

// Art & design (drawing · music · critique).
const ART = [
  '/imagine anything. The room paints it while you watch.',
  'Share your work and ask for one thing that works and one thing to fix.',
  '/aux queues a song on the room speakers. Everyone hears it.',
  'Drop an image in the chat and watch the AIs pick it apart.',
  'Stuck in a creative rut? Tell the AIs what you are working on.',
];

const POOLS = Object.freeze({
  learn: Object.freeze([...FOCUS, ...NEUTRAL]),
  research: Object.freeze([...FOCUS, ...RESEARCH, ...NEUTRAL]),
  life: Object.freeze([...CALM, ...NEUTRAL]),
  art: Object.freeze([...ART, ...NEUTRAL]),
});

/** The tip pool for a room's purpose, or null (the page's general pools apply). */
export function purposeHintPool(purpose) {
  return PURPOSE_KEYS.includes(purpose) && POOLS[purpose] ? POOLS[purpose] : null;
}

/** The build form's hop carries ?purpose= (BuildSpaceV2) so the FIRST tip is
 *  right before the room's status lands. A URLSearchParams (or anything with
 *  .get); only a real purpose key passes. */
export function purposeFromSearch(params) {
  let p = null;
  try { p = params && typeof params.get === 'function' ? params.get('purpose') : null; } catch (e) { p = null; }
  return PURPOSE_KEYS.includes(p) ? p : null;
}

export const PURPOSE_TIP_POOLS = POOLS; // tests
